import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Button,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";

interface IProject {
  name: string;
  status: string;
  description: string;
  tags: string[];
  githubUrl?: string;
  vercelUrl?: string;
}

const Projects = () => {
  const [projects, setProjects] = useState<IProject[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      const snapshot = await getDocs(collection(db, "projects"));
      const data = snapshot.docs.map((doc) => doc.data() as IProject);
      setProjects(data);
      setLoading(false);
    };

    fetchProjects();
  }, []);

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 64px)",
        px: { xs: 3, md: 10 },
        py: { xs: 6, md: 8 },
      }}
    >
      <Typography
        variant="overline"
        sx={{ color: "#64ffda", letterSpacing: "4px", fontSize: "0.85rem" }}
      >
        What I've built
      </Typography>

      <Typography
        variant="h3"
        sx={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 700,
          color: "#ffffff",
          mt: 1,
          mb: 6,
          fontSize: { xs: "2rem", md: "2.8rem" },
        }}
      >
        Projects
      </Typography>

      <Grid container spacing={4}>
        {loading ? (
          <Grid
            size={{ xs: 12 }}
            sx={{ display: "flex", justifyContent: "center", py: 10 }}
          >
            <CircularProgress sx={{ color: "#64ffda" }} />
          </Grid>
        ) : (
          projects.map((project) => (
            <Grid key={project.name} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                  border: "1px solid rgba(100, 255, 218, 0.15)",
                  borderRadius: 2,
                  transition: "border-color 0.2s, transform 0.2s",
                  "&:hover": {
                    borderColor: "rgba(100, 255, 218, 0.5)",
                    transform: "translateY(-4px)",
                  },
                }}
              >
                <CardContent
                  sx={{
                    flexGrow: 1,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontFamily: "Montserrat, sans-serif",
                        fontWeight: 600,
                        color: "#ffffff",
                      }}
                    >
                      {project.name}
                    </Typography>
                    <Chip
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              width: 7,
                              height: 7,
                              borderRadius: "50%",
                              flexShrink: 0,
                              ...(project.status === "Live (In Progress)" && {
                                backgroundColor: "#16a34a",
                              }),
                              ...(project.status === "In Development" && {
                                backgroundColor: "#d97706",
                              }),
                            }}
                          />
                          {project.status}
                        </Box>
                      }
                      size="small"
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                        ...(project.status === "Live (In Progress)" && {
                          backgroundColor: "rgba(34, 197, 94, 0.12)",
                          color: "#16a34a",
                          border: "1px solid rgba(34, 197, 94, 0.3)",
                        }),
                        ...(project.status === "In Development" && {
                          backgroundColor: "rgba(245, 158, 11, 0.12)",
                          color: "#d97706",
                          border: "1px solid rgba(245, 158, 11, 0.3)",
                        }),
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: "#a8b2d8",
                      lineHeight: 1.8,
                      mb: 3,
                      flexGrow: 1,
                    }}
                  >
                    {project.description}
                  </Typography>

                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {project.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        size="small"
                        sx={{
                          backgroundColor: "rgba(100, 255, 218, 0.08)",
                          color: "#64ffda",
                          border: "1px solid rgba(100, 255, 218, 0.3)",
                          fontFamily: "monospace",
                          fontSize: "0.7rem",
                        }}
                      />
                    ))}
                  </Box>
                </CardContent>

                {(project.githubUrl || project.vercelUrl) && (
                  <CardActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    {project.githubUrl && (
                      <Tooltip
                        title="This repository is private"
                        placement="bottom"
                        slotProps={{
                          tooltip: {
                            sx: {
                              font: "500 0.6rem 'Poppins', sans-serif",
                              letterSpacing: "0.3px",
                            },
                          },
                        }}
                      >
                        <Box>
                          <Button
                            href={project.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "#64ffda",
                              color: "#64ffda",
                              fontFamily: "Montserrat, sans-serif",
                              fontSize: "0.75rem",
                              letterSpacing: "1px",
                              "&:hover": {
                                backgroundColor: "rgba(100, 255, 218, 0.08)",
                                borderColor: "#64ffda",
                              },
                              "&.Mui-disabled": {
                                borderColor: "rgba(100, 255, 218, 0.2)",
                                color: "rgba(100, 255, 218, 0.3)",
                              },
                            }}
                            disabled
                          >
                            GitHub
                          </Button>
                        </Box>
                      </Tooltip>
                    )}
                    {project.vercelUrl && (
                      <Button
                        href={project.vercelUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="small"
                        variant="contained"
                        sx={{
                          backgroundColor: "#64ffda",
                          color: "#0f0f0f",
                          fontFamily: "Montserrat, sans-serif",
                          fontWeight: 700,
                          fontSize: "0.75rem",
                          letterSpacing: "1px",
                          "&:hover": { backgroundColor: "#45e5be" },
                        }}
                      >
                        Live Demo
                      </Button>
                    )}
                  </CardActions>
                )}
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
};

export default Projects;
